import {
  ChatMessageFromAPI,
  ChatMessageData,
  ChatMessageDataStreaming,
  MappedChatSession,
  AnswersOrAlertsResult,
  ParsedFlowContent,
  ChatRole,
  FlowStatus,
  OutputVariableName,
  AnswerMode,
} from './types';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

const HIDDEN_OUTPUT_VARIABLES = [
  'alert',
  'question',
  'answer',
  'final',
  'answer_mode',
];

const maybeParseJSON = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return content;
  }
};

const filterJsonKeys = (
  data: Record<string, unknown>
): Record<string, string | string[]> => {
  return Object.keys(data)
    .filter((key) => !key.includes('json'))
    .reduce(
      (obj, key) => {
        obj[key] = data[key] as string | string[];
        return obj;
      },
      {} as Record<string, string | string[]>
    );
};

const hasNonEmptyData = (data: Record<string, unknown>): boolean => {
  return Object.keys(data).some(
    (key) => !Array.isArray(data[key]) || (data[key] as unknown[]).length > 0
  );
};

const formatHeaderName = (name: string): string => {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getMessageContent = (
  data: ChatMessageData,
  name: keyof ChatMessageData
) => {
  return Array.isArray(data[name as keyof ChatMessageData])
    ? (data[name as keyof ChatMessageData] as string[]).join(' ')
    : (data[name as keyof ChatMessageData] as string);
};
export const chatMessageMapper = (
  messages: ChatMessageFromAPI[],
  genereationVarToMessageHeaderMapper: Record<string, string>
): MappedChatSession => {
  const intialChatSessionState: MappedChatSession = {
    messages: [],
    answers: [],
    numberOfNewMessages: 1,
    alert: '',
    answer_mode: AnswerMode.NONE,
    ready_for_action: false,
  };

  const mappedChatSession = messages.reduce(
    (acc: MappedChatSession, message: ChatMessageFromAPI) => {
      const { id, role, data, raw_content } = message;
      const isUserMessage = role === ChatRole.USER;

      let messageData = filterJsonKeys(data);
      let hasMessageData = hasNonEmptyData(messageData);
      const shouldParseRawContent =
        !hasMessageData && raw_content && role !== ChatRole.SYSTEM;

      if (shouldParseRawContent) {
        try {
          const parsedRawContent = maybeParseJSON(raw_content);
          const isValidObject =
            typeof parsedRawContent === 'object' && parsedRawContent !== null;
          if (isValidObject) {
            messageData = filterJsonKeys(parsedRawContent);
            hasMessageData = hasNonEmptyData(messageData);
          }
        } catch (e) {
          monitoringExceptionHelper(e, {
            extra: { context: 'parsing raw_content' },
          });
        }
      }

      const isSystemMessage = role === ChatRole.SYSTEM;
      const isAlertMessage =
        hasMessageData && typeof messageData?.alert === 'string';
      const isUserTextMessage =
        hasMessageData && isUserMessage && Boolean(messageData?.text);
      const isQuestionOnly =
        Boolean(data) && typeof messageData?.question === 'string';
      const isQuestionWithAnswers =
        hasMessageData &&
        typeof messageData?.question === 'string' &&
        Array.isArray(messageData?.answer);

      if (isSystemMessage) {
        let parsedContent: ParsedFlowContent = {};
        try {
          parsedContent = JSON.parse(raw_content);
        } catch (e) {
          parsedContent = {};
        }

        const isGenerationSuccessful = Boolean(
          parsedContent.flow_id &&
            parsedContent.next_actions &&
            parsedContent.next_actions.length > 0 &&
            parsedContent.status === FlowStatus.SUCCESS
        );
        const isGenerationFailed =
          !isGenerationSuccessful && parsedContent.status === FlowStatus.FAILED;

        if (isGenerationSuccessful) {
          acc.messages[acc.messages.length - 2].buildCompleted = true;
          acc.last_flow = parsedContent;
          acc.messages.push({
            id: `completed-${id}`,
            isUserMessage,
            isResponseLoading: false,
            messageContent: parsedContent.message || 'Completed',
            showFeedbackIcons: false,
            buildCompleted: true,
            role: ChatRole.ASSISTANT,
          });
        } else if (isGenerationFailed) {
          acc.last_flow = undefined;
          acc.messages.push({
            id: `failed-${id}`,
            isUserMessage,
            isResponseLoading: false,
            messageContent: parsedContent.message || 'Failed',
            showFeedbackIcons: false,
            buildCompleted: true,
            role: ChatRole.ASSISTANT,
          });
        }
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          messageContent: '',
          role,
        });
      } else if (isAlertMessage) {
        acc.alert = messageData?.alert as string;
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          messageContent: messageData?.alert as string,
          role,
        });
      } else if (isUserTextMessage) {
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          messageContent: data?.text,
          role,
        });
      } else if (isQuestionWithAnswers) {
        acc.answers = messageData?.answer as string[];
        acc.answer_mode =
          messageData?.answer_mode as (typeof AnswerMode)[keyof typeof AnswerMode];
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          messageContent: messageData?.question as string,
          role,
        });
      } else if (isQuestionOnly) {
        acc.answer_mode = '';
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        acc.answers = [];
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          messageContent: messageData?.question as string,
        });
      } else if (hasMessageData) {
        // has output variables that are not alert, question, answer
        let outputVars = Object.keys(messageData).filter(
          (name) => !HIDDEN_OUTPUT_VARIABLES.includes(name)
        );
        acc.answers = [];
        acc.answer_mode = '';
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        // ensure user_output is always the first message and populate other variables
        const hasUserOutput =
          messageData?.user_output && messageData?.user_output?.length > 0;
        if (hasUserOutput) {
          outputVars = [
            OutputVariableName.USER_OUTPUT,
            ...Object.keys(messageData).filter(
              (name) =>
                name !== OutputVariableName.USER_OUTPUT &&
                !HIDDEN_OUTPUT_VARIABLES.includes(name) &&
                messageData[name]?.length !== 0
            ),
          ];
        } else {
          outputVars = Object.keys(messageData).filter(
            (name) =>
              !HIDDEN_OUTPUT_VARIABLES.includes(name) &&
              messageData[name]?.length !== 0
          );
        }
        // since we are pushing more than one message, return number of new messages
        acc.numberOfNewMessages = outputVars.length;
        outputVars.forEach((name) => {
          if (name === OutputVariableName.USER_OUTPUT) {
            acc.messages.push({
              id: `${id}-${OutputVariableName.USER_OUTPUT}`,
              isUserMessage,
              isResponseLoading: false,
              messageContent: String(messageData?.user_output),
              role,
            });
            return;
          }
          const messageContent = getMessageContent(
            messageData as ChatMessageData,
            name as keyof ChatMessageData
          );
          if (messageContent !== '') {
            acc.messages.push({
              id: `${id}-${name}`,
              isUserMessage,
              isResponseLoading: false,
              header:
                genereationVarToMessageHeaderMapper[name] ||
                formatHeaderName(name),
              eventName: name,
              messageContent,
              role,
            });
          }
        });
      } else {
        acc.answers = [];
        acc.answer_mode = '';
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          messageContent: raw_content,
          role,
        });
      }
      return acc;
    },
    intialChatSessionState
  );
  mappedChatSession.messages = mappedChatSession.messages.filter(
    (message) => message.role !== ChatRole.SYSTEM
  );
  return mappedChatSession;
};

export const getAnswersOrAlertsByMessage = (
  data: ChatMessageDataStreaming
): AnswersOrAlertsResult => {
  const hasMessageData = hasNonEmptyData(data);
  const result: AnswersOrAlertsResult = {
    messages: [],
    answers: [],
    alert: '',
    numberOfNewMessages: 1,
  };

  const isAlertMessage = hasMessageData && typeof data?.alert === 'string';
  const isQuestionWithAnswers =
    hasMessageData &&
    typeof data?.question === 'string' &&
    Array.isArray(data?.answer);

  if (isAlertMessage) {
    result.answers = [];
    result.alert = data?.alert as string;
  } else if (isQuestionWithAnswers) {
    result.answers = data?.answer as string[];
    result.alert = '';
  } else {
    result.answers = [];
    result.alert = '';
  }
  return result;
};
